import React, { useState, useEffect } from 'react';
import styles from '../styles/Settings.module.scss';
import { Helmet } from 'react-helmet-async';
import Contacts from '../components/Contacts/Contacts';
import CategoriesBlock from '../components/CategoriesBlock/CategoriesBlock';
import { useInfoHints } from '../components/context/InfoHintsContext';
import Profile from '../components/Profile/Profile';
import Toggle from '../components/Toggle/Toggle';
import { triggerHaptic, hapticImpact } from '../utils/haptics';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const Settings = () => {
  const { enabled, setEnabled } = useInfoHints();
  const [freeMoney, setFreeMoney] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  useEffect(() => {
    const checkSubscription = async () => {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setNotificationsEnabled(!!subscription);
      }
    };
    checkSubscription();
  }, []);

  const [rememberCategory, setRememberCategory] = useState(
    () => localStorage.getItem('remember_last_category') === 'true'
  );

  const toggleRememberCategory = () => {
    const nextVal = !rememberCategory;
    setRememberCategory(nextVal);
    localStorage.setItem('remember_last_category', nextVal ? 'true' : 'false');

    if (!nextVal) {
      localStorage.removeItem('last_expense_category');
      localStorage.removeItem('last_income_category');
    }
  };

  const toggleNotifications = async () => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      alert('Ваш браузер или устройство не поддерживает push-уведомления');
      return;
    }

    setIsLoading(true);

    try {
      if (!notificationsEnabled) {
        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
          const registration = await navigator.serviceWorker.ready;
          const publicVapidKey =
            'BLD4QMp744m3iDq0p8wxPnp_f7mn8hIJSj8QXyjOJiGAgQRoeYIf0EElshM20fAO6DtuAUMBgqs8of6tQXa5fdE';

          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicVapidKey),
          });

          const response = await fetch('/api/auth/subscribe', {
            method: 'POST',
            body: JSON.stringify(subscription),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });

          if (!response.ok) throw new Error('Ошибка сервера при подписке');

          setNotificationsEnabled(true);
        } else {
          alert(
            'Вы запретили уведомления. Разрешите их в настройках браузера.'
          );
        }
      } else {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          const currentEndpoint = subscription.endpoint;

          await subscription.unsubscribe();

          await fetch('/api/auth/unsubscribe', {
            method: 'POST',
            body: JSON.stringify({ endpoint: currentEndpoint }),
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          });
        }

        setNotificationsEnabled(false);
      }
    } catch (error) {
      console.error('Ошибка переключения уведомлений:', error);
      alert(`Что-то пошло не так:\n${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const [isEventsEnabled, setIsEventsEnabled] = useState(
    localStorage.getItem('module_events_enabled') === 'true'
  );

  const toggleEvents = () => {
    const nextVal = !isEventsEnabled;
    setIsEventsEnabled(nextVal);
    localStorage.setItem('module_events_enabled', nextVal);
    triggerHaptic(hapticImpact.light);

    window.dispatchEvent(new Event('settingsUpdated'));
  };

  const [isCalendarEnabled, setIsCalendarEnabled] = useState(
    localStorage.getItem('module_calendar_enabled') === 'true'
  );

  const toggleCalendar = () => {
    const nextVal = !isCalendarEnabled;
    setIsCalendarEnabled(nextVal);
    localStorage.setItem('module_calendar_enabled', nextVal);
    triggerHaptic(hapticImpact.light);

    window.dispatchEvent(new Event('settingsUpdated'));
  };

  return (
    <>
      <Helmet>
        <title>ФА | Настройки</title>
      </Helmet>

      <div className={styles.settings__inner}>
        <div className={styles.settings__left}>
          <Profile />
        </div>

        <div className={styles.settings__right}>
          <div className={styles.settings}>
            <div className={styles.settings__block}>
              <div className={styles.settings__row}>
                <h4 className={styles.settings__name}>Модуль «События»</h4>
                <Toggle
                  enabled={isEventsEnabled}
                  onChange={toggleEvents}
                  ariaLabel='Переключить модуль событий'
                />
              </div>

              <div className={styles.settings__row}>
                <h4 className={styles.settings__name}>Модуль «Календарь»</h4>
                <Toggle
                  enabled={isCalendarEnabled}
                  onChange={toggleCalendar}
                  ariaLabel='Переключить модуль календаря'
                />
              </div>
            </div>

            <div className={styles.settings__block}>
              <div className={styles.settings__row}>
                <h4 className={styles.settings__name}>
                  Подсказки по интерфейсу
                </h4>
                <Toggle
                  enabled={enabled}
                  onChange={() => setEnabled((prev) => !prev)}
                  ariaLabel='Переключить подсказки'
                />
              </div>

              <div className={styles.settings__row}>
                <h4 className={styles.settings__name}>
                  Запоминать последнюю категорию
                </h4>
                <Toggle
                  enabled={rememberCategory}
                  onChange={toggleRememberCategory}
                  ariaLabel='Переключить запоминание последней категории'
                />
              </div>
            </div>

            <div className={styles.settings__block}>
              <div className={styles.settings__row}>
                <h4 className={styles.settings__name}>Запомнить меня</h4>
                <Toggle
                  enabled={rememberMe}
                  onChange={() => setRememberMe((prev) => !prev)}
                  ariaLabel='Переключить запоминание сессии'
                />
              </div>

              <div
                className={styles.settings__row}
                style={{
                  opacity: isLoading ? 0.5 : 1,
                  pointerEvents: isLoading ? 'none' : 'auto',
                }}>
                <h4 className={styles.settings__name}>
                  Ежедневные уведомления
                </h4>
                <Toggle
                  enabled={notificationsEnabled}
                  onChange={toggleNotifications}
                  ariaLabel='Переключить уведомления'
                />
              </div>
            </div>
          </div>
          <CategoriesBlock />
        </div>
      </div>
      <Contacts />
    </>
  );
};

export default Settings;
